import { Box, Icon as FalconIcon, FlexLayout, H4 } from '@deity/falcon-ui';
import { inputControlTypes, mapRecord, mapRecordToArray } from '@deity-io/di-data-basics';
import type { Component, Flow, IComponentInputPortDefinition } from '@deity-io/di-data-flows';
import { FlowLoader } from '@deity-io/di-data-flows';
import { isPropertyField } from '@deity-io/di-data-structure-editor';
import { Icon, Input } from '@deity-io/portal-ui';
import { JSONEditor } from 'components';
import FreeSoloList from 'components/FieldEditorControls/FreeSoloList';
import KeyValueTable, { KeyValidators } from 'components/FieldEditorControls/KeyValueTable';
import { Tooltip } from 'components/Tooltip';
import { FlowActionTypes, FlowContext } from 'contexts/FlowContext';
import _ from 'lodash';
import { useContext, useEffect, useRef } from 'react';
import type { FC } from 'react';
import FieldEditor from '../../../../FieldEditorControls/FieldEditor';
import ValidationErrorList from '../../ValidationErrorList';
import ControlVariables from './ControlVariables';
import EntityPicker from './EntityPicker';
import FlowPortSettings from './FlowPortSettings';
import FlowTriggerSettings from './FlowTriggerSettings';
import OptionsPicker from './OptionsPicker';
import ScalarTypePicker from './ScalarTypePicker';
import { SchemaPathPicker } from './StructurePathPicker';

interface FlowComponentSettingsProps {
  isEditable: boolean;
  // component?: Component;
}
const FlowComponentSettings: FC<FlowComponentSettingsProps> = ({ isEditable }) => {
  const ctx = useContext(FlowContext);
  let isExternalEntity = false;
  let component;
  let flow;
  // console.log('ctx.state.selectedComponent', ctx?.state.selectedComponent);
  if (ctx?.state && ctx.state.flow && ctx.state.selectedComponent && 'componentId' in ctx.state.selectedComponent) {
    if (ctx.state.selectedComponent.flowId) {
      const getEntities = ctx.state.flow.getEntityCallback();
      if (getEntities) {
        const entity = getEntities('flow', ctx.state.selectedComponent.flowId);
        if (entity) {
          flow = FlowLoader.tryLoad('entity', entity);
          component = flow?.getComponent(ctx.state.selectedComponent.componentId);
          isEditable = false;
          isExternalEntity = true;
        } else {
          // eslint-disable-next-line no-console
          console.warn('failed to fetch entity');
        }
      }
    } else {
      component = ctx.state.flow.getComponent(ctx.state.selectedComponent.componentId);
    }
  }
  if (!flow) {
    flow = ctx?.state.flow;
  }
  let body;
  if (!ctx?.state.selectedComponent) {
    body = [
      <FlexLayout key="empty_box" justifyContent="center" width="full" mt="xxl">
        <H4 fontWeight="bold" fontSize="xl" color="secondary">
          Select a component
        </H4>
      </FlexLayout>
    ];
  } else if (ctx?.state.selectedComponent && 'trigger' in ctx.state.selectedComponent) {
    body = <FlowTriggerSettings key="trigger_box" />;
  } else if (
    ctx?.state.selectedComponent &&
    'flowPort' in ctx.state.selectedComponent &&
    ctx?.state.selectedComponent.configPath
  ) {
    body = <FlowPortSettings key="settings_box" configPath={ctx?.state.selectedComponent.configPath} />;
  } else if (component) {
    body = [
      <CompConfig key="compConfig" flow={flow} isEditable={isEditable} component={component as Component} />,
      <CompErrorLog key="CompErrorLog" component={component} />
    ];
  }

  return (
    <FlexLayout p="xl" width="full" key={component?.key || '_none_'}>
      {body}
    </FlexLayout>
  );
};

export default FlowComponentSettings;

interface CompErrorLogProps {
  component?: Component;
}
const CompErrorLog: FC<CompErrorLogProps> = ({ component }) => {
  const ctx = useContext(FlowContext);
  if (component && ctx?.state.flowSummary) {
    return (
      <ValidationErrorList
        messages={ctx?.state.flowSummary.messages.filter(message => message.component === component.key)}
        onClick={componentId => {
          ctx?.dispatch({
            type: FlowActionTypes.SELECT_COMPONENT,
            payload: { componentId }
          });
        }}
      />
    );
  }

  return null;
};

interface CompConfigProps {
  isEditable: boolean;
  component: Component;
  flow: Flow;
}
const CompConfig: FC<CompConfigProps> = ({ isEditable, component, flow }) => {
  const ctx = useContext(FlowContext);

  const updateProperty = (key: Array<string>, entityId: any, refresh = false) => {
    if (ctx?.state.selectedComponent && 'componentId' in (ctx?.state.selectedComponent || {})) {
      ctx.dispatch({
        type: FlowActionTypes.UPDATE_COMPONENT,
        // @ts-ignore
        payload: { compId: ctx.state.selectedComponent.componentId, key, val: entityId, regenerateHash: refresh }
      });
    }
  };

  const editableProperties = mapRecord(component.getProperties(), property => {
    if (!(property.type === inputControlTypes.port || property.type === inputControlTypes.hidden)) {
      return property;
    }

    return undefined;
  });

  return (
    <>
      {component && (
        <FlowComponentInfo
          key="FlowComponentInfo"
          type={component.definition.type}
          displayName={component.definition?.displayName}
          description={component.definition?.description}
          icon={component.definition?.icon}
          componentUuid={component.key}
        />
      )}
      {component && (
        <FlexLayout key="comp_description_box" justifyContent="center" width="full" mt="xs">
          <Input
            key="comp_description"
            multiline
            fullWidth
            value={component.config.description || null}
            disabled={!isEditable}
            label={component.definition.description}
            minRows={3}
            onChange={e => {
              const { value } = e.target;
              ctx?.dispatch({
                type: FlowActionTypes.UPDATE_COMPONENT,
                payload: {
                  compId: component?.key,
                  key: ['description'],
                  val: value.length ? value : undefined,
                  regenerateHash: false
                }
              });
            }}
            onBlur={e => {
              // OnBlur with regenerate hash = true
              const value = e.target.value.trim();
              ctx?.dispatch({
                type: FlowActionTypes.UPDATE_COMPONENT,
                payload: {
                  compId: component?.key,
                  key: ['description'],
                  val: value.length ? value : undefined,
                  regenerateHash: true // needed to update the UI
                }
              });
            }}
          />
        </FlexLayout>
      )}

      {!Object.keys(editableProperties).length && (
        <FlexLayout key="no_comp_settings" justifyContent="center" width="full" mt="xxl">
          <H4 key="no_comp_settings_header" fontWeight="bold" fontSize="xl" color="secondary">
            No extra settings available.
          </H4>
        </FlexLayout>
      )}
      {mapRecordToArray(editableProperties, (editableProperty, property) => {
        const propertyErrors =
          ctx?.state?.flowSummary?.messages.filter(
            message => message.component === component.key && message.path === `${component.key}.config.${property}`
          ) || [];
        if (editableProperty.inputControl === inputControlTypes.schemaPathPicker && isPropertyField(editableProperty)) {
          const messages: Array<string> = [];
          const schema = flow!.handleSchemaProvides(component, (editableProperty as any).schemaProviders, messages);

          return (
            <SchemaPathPicker
              key={property}
              type="schema"
              label={editableProperty.name || property}
              isHeaderHidden
              property={property}
              value={component.config[property] || editableProperty.defaultValue}
              description={editableProperty.description || ''}
              schema={schema}
              onChange={(key, val) => updateProperty([key], val)}
              onBlur={(key, val) => updateProperty([key], val, true)}
              onSelect={(key, val) => updateProperty([key], val, true)}
              isEditable={isEditable}
              // error={propertyErrors.length > 0}
            />
          );
        }

        if (
          editableProperty.inputControl === inputControlTypes.schemaObjectPathPicker &&
          isPropertyField(editableProperty)
        ) {
          const messages: Array<string> = [];
          const schema = flow!.getInputDataSchema(
            component,
            { key: 'input', ...component.definition.ports['input'] } as IComponentInputPortDefinition & {
              key: string;
            },
            messages
          );

          return (
            <SchemaPathPicker
              key={property}
              type="object"
              label={editableProperty.name || property}
              isHeaderHidden
              property={property}
              value={component.config[property] || editableProperty?.defaultValue}
              description={editableProperty.description || ''}
              schema={schema}
              // error={propertyErrors.length > 0}
              onChange={(key, val) => updateProperty([key], val)}
              onBlur={(key, val) => updateProperty([key], val, true)}
              onSelect={(key, val) => updateProperty([key], val, true)}
              isEditable={isEditable}
            />
          );
        }
        if (
          (editableProperty.inputControl === inputControlTypes.string ||
            editableProperty.inputControl === inputControlTypes.number ||
            editableProperty.inputControl === inputControlTypes.boolean) &&
          isPropertyField(editableProperty)
        ) {
          return (
            <FieldEditor
              key={property}
              property={property}
              intValue={component.config[property] || editableProperty.defaultValue}
              inputControl={editableProperty.inputControl}
              notEmpty={editableProperty.notEmpty}
              required={editableProperty.required}
              description={editableProperty.description}
              isEditable={isEditable}
              // error={propertyErrors.length > 0}
              onChange={(key, val) => updateProperty([key], val)}
              onBlur={(key, val) => updateProperty([key], val, true)}
            />
          );
        }
        if (editableProperty.inputControl === inputControlTypes.mapping) {
          return (
            <EntityPicker
              label={editableProperty.name || property}
              key={property}
              property={property}
              entityUUID={component.config[property]}
              error={propertyErrors.length > 0}
              description={editableProperty.description || ''}
              entity="mapping"
              isEditable={isEditable}
              onChange={(key, entityId) => updateProperty([key], entityId, true)}
              showEntityLink
            />
          );
        }
        if (editableProperty.inputControl === inputControlTypes.endpointPicker) {
          return (
            <EntityPicker
              label={editableProperty.name || property}
              key={property}
              property={property}
              entityUUID={component.config[property]}
              description={editableProperty.description || ''}
              error={propertyErrors.length > 0}
              entity="endpoint"
              isEditable={isEditable}
              onChange={(key, entityId) => updateProperty([key], entityId, true)}
              showEntityLink
            />
          );
        }
        if (editableProperty.inputControl === inputControlTypes.controlVariables) {
          return (
            <ControlVariables
              label={editableProperty.name || property}
              key={property}
              property={property}
              controlConfig={ctx?.state.flow?.getFullRuntimeConfig(component)}
              description={editableProperty.description || ''}
              error={propertyErrors.length > 0}
              onChange={(key, val) => {
                updateProperty([key], val, false);
              }}
              onBlur={(key, val) => {
                updateProperty([key], val, true);
              }}
            />
          );
        }
        if (editableProperty.inputControl === inputControlTypes.structurePicker) {
          return (
            <EntityPicker
              key={property}
              property={property}
              entityUUID={component.config[property]}
              description={editableProperty.description || ''}
              entity="structure"
              label="Select a Structure"
              name={editableProperty.name || property}
              error={propertyErrors.length > 0}
              isEditable={isEditable}
              showEntityLink
              onChange={(key, val) => {
                updateProperty([key], val, true);
              }}
            />
          );
        }

        if (editableProperty.inputControl === inputControlTypes.partialFlow) {
          return (
            <EntityPicker
              key={property}
              property={property}
              entityUUID={component.config[property]}
              description={editableProperty.description || ''}
              entity="partialFlow"
              label="Select a Flow"
              name={editableProperty.name || property}
              error={propertyErrors.length > 0}
              isEditable={isEditable}
              showEntityLink
              onChange={(key, val) => {
                updateProperty([key], val, true);
              }}
            />
          );
        }

        if (editableProperty.inputControl === inputControlTypes.exceptionFlow) {
          return (
            <EntityPicker
              key={property}
              property={property}
              entityUUID={component.config[property]}
              description={editableProperty.description || ''}
              entity="exceptionFlow"
              label="Select a Flow"
              name={editableProperty.name || property}
              error={propertyErrors.length > 0}
              isEditable={isEditable}
              showEntityLink
              onChange={(key, val) => {
                updateProperty([key], val, true);
              }}
            />
          );
        }

        if (editableProperty.inputControl === inputControlTypes.options && isPropertyField(editableProperty)) {
          return (
            <OptionsPicker
              key={property}
              name={editableProperty.name || property}
              property={property}
              value={component.config[property]}
              description={editableProperty.description || ''}
              options={editableProperty.options || []}
              isEditable={isEditable}
              error={propertyErrors.length > 0}
              disableClearable={!_.isUndefined(editableProperty.defaultValue)}
              onSelect={val => updateProperty([property], val, true)}
            />
          );
        }
        if (editableProperty.inputControl === inputControlTypes.jsonEditor) {
          const canChange =
            component.config[property] === undefined ||
            (Array.isArray(component.config[property]) && component.config[property].length === 0) ||
            Object.keys(component.config[property]).length === 0;

          const isArray = Array.isArray(component.config[property]);

          const tmpIcon = canChange ? (
            <Tooltip content={isArray ? 'Change to Object' : 'Change to Array'} direction="left">
              <Icon
                icon="dragIndicator"
                color="secondary"
                stroke="transparent"
                onClick={e => updateProperty([property], isArray ? undefined : [], true)}
              />
            </Tooltip>
          ) : undefined;

          return (
            <JSONEditor
              key={property}
              label={editableProperty.name || property}
              description={editableProperty.description || ''}
              value={component.config[property] || {}}
              locked={isEditable === false}
              onUpdate={val => updateProperty([property], val, true)}
              // error={propertyErrors.length > 0}
              toolIcons={tmpIcon}
            />
          );
        }
        if (editableProperty.inputControl === inputControlTypes.dictionary) {
          return (
            <FlexLayout
              p="xs"
              mt="xs"
              mb="xs"
              bg="panelLight"
              borderRadius="small"
              css={{ minHeight: '78px' }}
              width="full"
            >
              <FlexLayout alignItems="center" width="full">
                <FlexLayout alignItems="center" width="full" flexDirection="row" justifyContent="space-between" mb="xs">
                  <H4 fontWeight="bold" mr="xs" style={{ textTransform: 'capitalize' }}>
                    {editableProperty.name || property}
                  </H4>
                  {editableProperty.description && (
                    <Tooltip key="tooltip" content={editableProperty.description} direction="left">
                      <FalconIcon src="helpOutline" color="secondary" stroke="transparent" />
                    </Tooltip>
                  )}
                </FlexLayout>
                <Box width="full">
                  <KeyValueTable
                    key={property}
                    isEditable={isEditable}
                    keyValidator={KeyValidators.NO_WHITE_SPACE}
                    persistedValue={component.config[property]}
                    onTableValueChange={(key, val) => updateProperty([property], val, false)}
                    onTableValueBlur={(key, val) => updateProperty([property], val, true)}
                  />
                </Box>
              </FlexLayout>
            </FlexLayout>
          );
        }
        if (editableProperty.inputControl === inputControlTypes.password && isPropertyField(editableProperty)) {
          return (
            <FieldEditor
              key={property}
              property={property}
              intValue={component.config[property] || editableProperty.defaultValue}
              inputControl={inputControlTypes.string}
              notEmpty={editableProperty.notEmpty}
              required={editableProperty.required}
              description={editableProperty.description}
              isEditable={isEditable}
              isPassword
              // error={propertyErrors.length > 0}
              onChange={(key, val) => updateProperty([key], val)}
              onBlur={(key, val) => updateProperty([key], val, true)}
            />
          );
        }
        if (editableProperty.inputControl === inputControlTypes.freeSoloList) {
          return (
            <FlexLayout
              key={`freeSolo_${property}`}
              p="xs"
              mt="xs"
              mb="xs"
              bg="panelLight"
              borderRadius="small"
              width="full"
            >
              <FlexLayout key={`freeSoloHeader_${property}`} alignItems="center" width="full">
                <FlexLayout width="full" alignItems="center" justifyContent="space-between">
                  <H4 fontWeight="bold" mr="xs" key="header" style={{ textTransform: 'capitalize' }}>
                    {editableProperty.name || property}
                  </H4>
                  {editableProperty.description && (
                    <Tooltip key="tooltip" content={editableProperty.description} direction="left">
                      <FalconIcon src="helpOutline" color="secondary" stroke="transparent" />
                    </Tooltip>
                  )}
                </FlexLayout>
              </FlexLayout>
              <FreeSoloList
                key={property}
                name={property}
                inputControlType={inputControlTypes.string}
                locked={isEditable === false}
                list={component.config[property] || []}
                required={editableProperty.required}
                notEmpty={editableProperty.notEmpty}
                onChange={(soloIndex, val) => updateProperty([property, ...soloIndex], val)}
                // error={propertyErrors.length > 0}
              />
            </FlexLayout>
          );
        }

        if (editableProperty.inputControl === inputControlTypes.scalarTypePicker && isPropertyField(editableProperty)) {
          return (
            <ScalarTypePicker
              key={property}
              name={editableProperty.name || property}
              property={property}
              value={component.config[property]}
              description={editableProperty.description || ''}
              options={editableProperty.options || []}
              isEditable={isEditable}
              error={propertyErrors.length > 0}
              disableClearable={!_.isUndefined(editableProperty.defaultValue)}
              onSelect={val => updateProperty([property], val, true)}
            />
          );
        }

        if (
          editableProperty.inputControl &&
          editableProperty.inputControl !== inputControlTypes.port &&
          editableProperty.inputControl !== inputControlTypes.hidden
        ) {
          // extra debug info if no input control for component
          // eslint-disable-next-line no-console
          console.warn(`Control not found "${editableProperty.inputControl}"`);
        }

        return undefined;
      })}
    </>
  );
};

interface FlowComponentInfoProps {
  type: string;
  displayName?: string;
  description?: string;
  icon?: string;
  componentUuid?: string;
}
const FlowComponentInfo: FC<FlowComponentInfoProps> = ({ type, displayName, description, icon, componentUuid }) => {
  const svgWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (svgWrapperRef.current && icon) {
      svgWrapperRef.current.innerHTML = icon;
    }
  }, [type]);

  return (
    <FlexLayout justifyContent="space-between" width="full" alignItems="center" mb="xs">
      <FlexLayout alignItems="center">
        <Box ref={svgWrapperRef} mr="xs" />
        <H4 fontWeight="bold" fontSize="lg" style={{ textTransform: 'uppercase' }}>
          {displayName}
        </H4>
      </FlexLayout>
      <Tooltip direction="left" content={description}>
        <FalconIcon src="helpOutline" color="secondary" stroke="transparent" />
      </Tooltip>
      <H4>
        <small>{componentUuid}</small>
      </H4>
    </FlexLayout>
  );
};
